import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2, CalendarDays, Clock, User, Mail, Phone, Building2, Wrench } from "lucide-react";
import { SERVICES } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const TIME_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
  "15:00", "15:30", "16:00", "16:30", "17:00",
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service: "",
  preferred_date: "",
  preferred_time: "",
  message: "",
};

function Field({ label, icon: Icon, required, children }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1.5 text-[13px] font-medium text-slate-300 mb-2">
        {Icon && <Icon size={13} className="text-cyan-400" />}
        {label}
        {required && <span className="text-cyan-400">*</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 text-[15px] text-white placeholder:text-slate-500 " +
  "focus:outline-none focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/20 transition";

export default function ContactForm() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const today = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name, email and phone number.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        ...form,
        company: form.company || null,
        service: form.service || null,
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time || null,
        message: form.message || null,
      });
      toast.success(res.data?.message || "Request received — we'll be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Something went wrong sending your request. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="glass rounded-3xl p-7 md:p-10"
      data-testid="contact-form"
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full name" icon={User} required>
          <input
            data-testid="contact-input-name"
            className={inputCls}
            placeholder="Jane Doe"
            value={form.name}
            onChange={set("name")}
            autoComplete="name"
            maxLength={120}
          />
        </Field>

        <Field label="Work email" icon={Mail} required>
          <input
            data-testid="contact-input-email"
            className={inputCls}
            type="email"
            placeholder="jane@company.com"
            value={form.email}
            onChange={set("email")}
            autoComplete="email"
          />
        </Field>

        <Field label="Phone number" icon={Phone} required>
          <input
            data-testid="contact-input-phone"
            className={inputCls}
            type="tel"
            placeholder="+1 (555) 000-0000"
            value={form.phone}
            onChange={set("phone")}
            autoComplete="tel"
            maxLength={40}
          />
        </Field>

        <Field label="Company (optional)" icon={Building2}>
          <input
            data-testid="contact-input-company"
            className={inputCls}
            placeholder="Acme Corp"
            value={form.company}
            onChange={set("company")}
            autoComplete="organization"
            maxLength={200}
          />
        </Field>

        <Field label="Service of interest" icon={Wrench}>
          <select
            data-testid="contact-input-service"
            className={`${inputCls} appearance-none cursor-pointer [&>option]:bg-slate-900`}
            value={form.service}
            onChange={set("service")}
          >
            <option value="">Select a service…</option>
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.title}>{s.title}</option>
            ))}
            <option value="Other / Not sure yet">Other / Not sure yet</option>
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Preferred date" icon={CalendarDays}>
            <input
              data-testid="contact-input-date"
              className={inputCls}
              type="date"
              min={today}
              value={form.preferred_date}
              onChange={set("preferred_date")}
            />
          </Field>
          <Field label="Preferred time" icon={Clock}>
            <select
              data-testid="contact-input-time"
              className={`${inputCls} appearance-none cursor-pointer [&>option]:bg-slate-900`}
              value={form.preferred_time}
              onChange={set("preferred_time")}
            >
              <option value="">Any time</option>
              {TIME_SLOTS.map((t) => (
                <option key={t} value={t}>{t} ET</option>
              ))}
            </select>
          </Field>
        </div>
      </div>

      <div className="mt-5">
        <Field label="Tell us about your project (optional)">
          <textarea
            data-testid="contact-input-message"
            className={`${inputCls} min-h-[130px] resize-y`}
            placeholder="Current stack, pain points, timelines…"
            value={form.message}
            onChange={set("message")}
            maxLength={4000}
          />
        </Field>
      </div>

      <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-xs text-slate-500 max-w-sm">
          We reply within one business day. Your details are only used to schedule your
          consultation — never shared or sold.
        </p>
        <button
          type="submit"
          disabled={sending}
          data-testid="contact-submit"
          className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-cyan-400 text-slate-950 font-semibold hover:bg-cyan-300 disabled:opacity-60 disabled:cursor-not-allowed transition shadow-[0_0_30px_rgba(0,229,255,0.35)]"
        >
          {sending ? (
            <>
              <Loader2 size={17} className="animate-spin" /> Sending…
            </>
          ) : (
            <>
              Request consultation
              <Send size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </>
          )}
        </button>
      </div>
    </motion.form>
  );
}
